/**
 * Centralized Voice Recognition Service
 * Manages a single persistent SpeechRecognition instance with proper state tracking
 * Ensures listeners are attached and ready before user speaks
 */

class VoiceRecognitionService {
  constructor() {
    this.recognition = null;
    this.isInitialized = false;
    this.isRunning = false;
    this.isStarting = false;
    this.activeFieldId = null;
    this.callbacks = {
      onTranscript: null,
      onError: null,
      onStart: null,
      onEnd: null
    };
    this.initializationPromise = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.retryDelay = 1000; // Start with 1 second
  }

  /**
   * Initialize the speech recognition instance
   * Returns a promise that resolves when fully initialized
   */
  async initialize() {
    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Return immediately if already initialized
    if (this.isInitialized && this.recognition) {
      return Promise.resolve();
    }

    this.initializationPromise = new Promise((resolve, reject) => {
      if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
        reject(new Error('Speech recognition not supported'));
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';
      this.recognition.maxAlternatives = 1;

      // Set up event handlers
      this.recognition.onstart = () => {
        console.log('[VoiceService] Recognition started');
        this.isRunning = true;
        this.isStarting = false;
        if (this.callbacks.onStart) {
          this.callbacks.onStart();
        }
      };

      this.recognition.onresult = (event) => {
        console.log('[VoiceService] Recognition result received');
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }

        // Deliver transcript via callback
        if (finalTranscript && this.callbacks.onTranscript) {
          this.callbacks.onTranscript(finalTranscript, this.activeFieldId);
        }
      };

      this.recognition.onerror = (event) => {
        console.error('[VoiceService] Recognition error:', event.error);
        this.isRunning = false;
        this.isStarting = false;
        
        if (event.error === 'aborted') {
          // Normal stop, don't report as error
          return;
        }

        // Handle network errors with automatic retry
        if (event.error === 'network' && this.retryCount < this.maxRetries) {
          this.retryCount++;
          const delay = this.retryDelay * Math.pow(2, this.retryCount - 1); // Exponential backoff
          console.log(`[VoiceService] Network error, retrying in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries})`);
          
          setTimeout(() => {
            if (this.activeFieldId) {
              this.startRecording(this.activeFieldId).catch(err => {
                console.error('[VoiceService] Retry failed:', err);
              });
            }
          }, delay);
          return;
        }

        // Reset retry count on non-network errors or max retries reached
        if (event.error !== 'network') {
          this.retryCount = 0;
        }

        if (this.callbacks.onError) {
          let errorMessage = 'Recognition error occurred';
          if (event.error === 'no-speech') {
            errorMessage = 'No speech detected. Please try speaking again.';
          } else if (event.error === 'network') {
            errorMessage = 'Network error persists. Please check your connection and try again.';
          } else {
            errorMessage = `Error: ${event.error}. Please try again.`;
          }
          this.callbacks.onError(errorMessage);
        }
      };

      this.recognition.onend = () => {
        console.log('[VoiceService] Recognition ended');
        const wasRunning = this.isRunning;
        this.isRunning = false;
        this.isStarting = false;
        
        // Auto-restart if still active
        if (this.activeFieldId && wasRunning) {
          console.log('[VoiceService] Auto-restarting recognition');
          setTimeout(() => {
            if (this.activeFieldId && !this.isRunning && !this.isStarting) {
              this.startRecording(this.activeFieldId).catch(err => {
                console.error('[VoiceService] Auto-restart failed:', err);
              });
            }
          }, 100);
        } else if (this.callbacks.onEnd) {
          this.callbacks.onEnd();
        }
      };

      this.isInitialized = true;
      console.log('[VoiceService] Initialized successfully');
      resolve();
    });

    return this.initializationPromise;
  }

  /**
   * Register callbacks for recognition events
   */
  setCallbacks({ onTranscript, onError, onStart, onEnd }) {
    this.callbacks = {
      onTranscript: onTranscript || this.callbacks?.onTranscript,
      onError: onError || this.callbacks?.onError,
      onStart: onStart || this.callbacks?.onStart,
      onEnd: onEnd || this.callbacks?.onEnd
    };
    console.log('[VoiceService] Callbacks registered');
  }

  /**
   * Start recording for a specific field
   * Ensures initialization and proper state before starting
   */
  async startRecording(fieldId) {
    console.log('[VoiceService] Start recording requested for:', fieldId);

    // Prevent multiple simultaneous start attempts
    if (this.isStarting) {
      console.log('[VoiceService] Already starting, ignoring duplicate request');
      return;
    }

    // Ensure initialized
    await this.initialize();

    // Stop existing recording if running
    if (this.isRunning) {
      console.log('[VoiceService] Stopping existing recording');
      await this.stopRecording();
      // Wait for clean stop
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Set active field BEFORE starting
    this.activeFieldId = fieldId;
    console.log('[VoiceService] Active field set to:', fieldId);

    // Ensure callbacks are ready
    if (!this.callbacks?.onTranscript) {
      throw new Error('Transcript callback not registered');
    }

    // Start recognition
    try {
      this.isStarting = true;
      this.recognition?.start();
      console.log('[VoiceService] Recognition start() called');
      
      // Wait for actual start confirmation
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.isStarting = false;
          reject(new Error('Recognition start timeout'));
        }, 2000);

        const originalOnStart = this.callbacks.onStart;
        this.callbacks.onStart = () => {
          clearTimeout(timeout);
          this.callbacks.onStart = originalOnStart;
          // Reset retry count on successful start
          this.retryCount = 0;
          if (originalOnStart) originalOnStart();
          resolve();
        };
      });
    } catch (error) {
      this.activeFieldId = null;
      this.isRunning = false;
      this.isStarting = false;
      throw error;
    }
  }

  /**
   * Stop recording
   */
  async stopRecording() {
    console.log('[VoiceService] Stop recording requested');
    
    // Clear active field first to prevent auto-restart
    this.activeFieldId = null;
    
    if (this.recognition && this.isRunning) {
      try {
        this.recognition?.stop();
        console.log('[VoiceService] Recognition stop() called');
      } catch (error) {
        console.error('[VoiceService] Error stopping recognition:', error);
      }
    }
    
    this.isRunning = false;
  }

  /**
   * Check if recognition is currently running
   */
  getIsRunning() {
    return this.isRunning;
  }

  /**
   * Get active field ID
   */
  getActiveFieldId() {
    return this.activeFieldId;
  }

  /**
   * Check if speech recognition is supported
   */
  isSupported() {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.recognition) {
      try {
        this.recognition?.stop();
      } catch (error) {
        // Ignore cleanup errors
      }
      this.recognition = null;
    }
    this.isInitialized = false;
    this.isRunning = false;
    this.isStarting = false;
    this.activeFieldId = null;
    this.callbacks = {
      onTranscript: null,
      onError: null,
      onStart: null,
      onEnd: null
    };
    this.initializationPromise = null;
    console.log('[VoiceService] Destroyed');
  }
}

// Export singleton instance
export const voiceRecognitionService = new VoiceRecognitionService();
